import { useMemo } from 'react';
import { getClass, getASILevels } from '@character-creator/data';
import type { CharacterDraft } from '../stores/characterCreationStore';
import type { DockTab, IdentitySubTab } from '../stores/types';
import { useCharacterCreationStore } from '../stores/characterCreationStore';

export interface ReadinessItem {
  id: string;
  label: string;
  complete: boolean;
  required: boolean;
  dockTab: DockTab;
  subTab?: IdentitySubTab;
}

export interface CharacterReadiness {
  items: ReadinessItem[];
  requiredComplete: number;
  requiredTotal: number;
  advisoryComplete: number;
  advisoryTotal: number;
  percentage: number;
  isReady: boolean;
}

/**
 * Pure function: compute character readiness from a draft.
 * Extracted so it can be unit-tested without React.
 */
export function getCharacterReadiness(draft: CharacterDraft): CharacterReadiness {
  // --- Required items ---
  const hasName = draft.name.trim().length > 0;
  const hasRace = draft.raceId !== null;
  const hasClass = draft.classId !== null || draft.confirmedCommoner;
  const hasBackground = draft.backgroundId !== null || draft.confirmedNoBackground;

  // Abilities: scores set + all ASI choices made
  const hasAbilityScores = Object.values(draft.abilityScores).some((s) => s !== 8);
  const allAsiLevels = draft.classLevels.flatMap((cl) =>
    getASILevels(cl.classId)
      .filter((l) => l <= cl.level)
      .map((l) => ({ classId: cl.classId, level: l }))
  );
  const classLevelMap = new Map(draft.classLevels.map((c) => [c.classId, c]));
  const allAsiMade = allAsiLevels.every(({ classId, level }) => {
    const cl = classLevelMap.get(classId);
    return cl?.asiChoices?.[level] != null;
  });
  const hasAbilities = hasAbilityScores && (allAsiLevels.length === 0 || allAsiMade);

  // --- Advisory items ---
  const hasEquipment =
    Object.keys(draft.equipmentSelections).length > 0 || draft.customEquipment.length > 0;

  // Spells: auto-complete for non-casters
  let hasSpells = true;
  if (draft.classLevels.length > 0) {
    const primaryClass = getClass(draft.classLevels[0].classId);
    if (primaryClass?.spellcasting) {
      hasSpells = draft.selectedCantrips.length > 0 || draft.knownSpells.length > 0 || draft.preparedSpells.length > 0;
    }
  }

  const items: ReadinessItem[] = [
    { id: 'name', label: 'Name', complete: hasName, required: true, dockTab: 'identity', subTab: 'basics' },
    { id: 'race', label: 'Race', complete: hasRace, required: true, dockTab: 'identity', subTab: 'race' },
    { id: 'class', label: 'Class', complete: hasClass, required: true, dockTab: 'identity', subTab: 'class' },
    { id: 'abilities', label: 'Abilities', complete: hasAbilities, required: true, dockTab: 'identity', subTab: 'abilities' },
    { id: 'background', label: 'Background', complete: hasBackground, required: true, dockTab: 'identity', subTab: 'background' },
    { id: 'equipment', label: 'Equipment', complete: hasEquipment, required: false, dockTab: 'equipment' },
    { id: 'spells', label: 'Spells', complete: hasSpells, required: false, dockTab: 'spells' },
  ];

  const requiredItems = items.filter((i) => i.required);
  const advisoryItems = items.filter((i) => !i.required);
  const requiredComplete = requiredItems.filter((i) => i.complete).length;
  const advisoryComplete = advisoryItems.filter((i) => i.complete).length;

  return {
    items,
    requiredComplete,
    requiredTotal: requiredItems.length,
    advisoryComplete,
    advisoryTotal: advisoryItems.length,
    percentage: Math.round((requiredComplete / requiredItems.length) * 100),
    isReady: requiredComplete === requiredItems.length,
  };
}

/**
 * React hook: thin memoized wrapper around getCharacterReadiness.
 */
export function useCharacterReadiness(): CharacterReadiness {
  const { draft } = useCharacterCreationStore();
  return useMemo(() => getCharacterReadiness(draft), [draft]);
}
